import { computed } from 'vue'
import { useQueries, useQueryClient } from '@tanstack/vue-query'
import { useAccountStore } from 'stores/account'
import { getScanStatusQueryOptions } from 'src/composables/scan/query/useScanResultQuery'

/**
 * Per-site scan status queries with polling while a scan is in progress.
 *
 * Creates one `[QUERY_KEYS.SCAN_STATUS, siteId]` query per site ID.
 * Queries fire in parallel and each resolves independently.
 * Polling continues at 15s intervals while status is `SCAN_STATUS.PENDING` or `SCAN_STATUS.SCANNING`.
 * Each fetch broadcasts updated scan data to other tabs via `scanResultQueryFn`.
 *
 * @param {import('vue').ComputedRef<string[]>} siteIds - reactive list of site IDs to poll
 */
export function useScanPollingQueries(siteIds) {
  const accountStore = useAccountStore()
  const queryClient = useQueryClient()
  const enableQuery = computed(() => !!accountStore.accountId && !accountStore.isLoading)

  const results = useQueries({
    queries: computed(() =>
      siteIds.value.map((siteId) => ({
        ...getScanStatusQueryOptions({ queryClient, siteId, shouldPoll: true }),
        enabled: enableQuery.value && !!siteId,
      }))
    ),
  })

  const scanStatusMap = computed(() => {
    const map = {}
    siteIds.value.forEach((siteId, index) => {
      const query = results.value?.[index]
      if (!query) return
      map[siteId] = {
        data: query.data ?? null,
        isLoading: query.isLoading,
        error: query.error ?? null,
      }
    })
    return map
  })

  return { scanStatusMap }
}
